import React from 'react'
import ReactMarkdown from 'react-markdown'
import PropTypes from 'prop-types'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Hero from '@components/hero'
import Grid from '@material-ui/core/Grid'
import { formattedAddress } from '@layouts/academy/utils'
import Container from '@material-ui/core/Container'
import { useTheme } from '@material-ui/core/styles'
import QuickLinks from './quick-links'
import { useStyles } from './academy.styles'
import Header from './information/header'
import ContactInfoSection from './information/contact-info-section'

const Description = ({ description }) => {
  const classes = useStyles()
  if (description) {
    return (
      <ReactMarkdown
        skipHtml
        className={classes.descriptionText}
        components={{
          p: ({ node, ordered, ...rest }) => {
            return (
              <Box mb={2}>
                <Typography
                  {...rest}
                  variant="body1"
                  style={{ fontWeight: 600 }}
                />
              </Box>
            )
          },
          h1: ({ node, ordered, ...rest }) => <Typography color="secondary" {...rest} variant="h1" gutterBottom />,
          h2: ({ node, ordered, ...rest }) => <Typography color="secondary" {...rest} variant="h2" gutterBottom />,
          h3: ({ node, ordered, ...rest }) => <Typography color="secondary" {...rest} variant="h3" gutterBottom />,
          h4: ({ node, ordered, ...rest }) => <Typography color="secondary" {...rest} variant="h4" gutterBottom />,
          h5: ({ node, ordered, ...rest }) => <Typography color="secondary" {...rest} variant="h5" gutterBottom />,
          h6: ({ node, ordered, ...rest }) => <Typography color="secondary" {...rest} variant="h6" gutterBottom />,
          ul: ({ node, ordered, ...rest }) => <ul {...rest} style={{ marginTop: '0.25em' }} />,
          li: ({ node, ordered, ...rest }) => <li><Typography {...rest} /></li>,
        }}
      >
        {description}
      </ReactMarkdown>
    )
  }
  return null
}

export const buildContactInfo = (academy) => {
  const email = academy?.email
  const phoneNumber = academy?.phoneNumber
  const primaryFacility = academy?.facilities?.[0]

  const facility = academy?.slug.startsWith('pga-hope') ? null : {
    name: academy?.name,
    zip: primaryFacility?.zip,
    address1: primaryFacility?.address1,
    formattedAddress: formattedAddress(primaryFacility),
  }

  return {
    email,
    phoneNumber,
    facility,
  }
}

const Information = ({ academy, bgColor }) => {
  const {
    description,
    featuredImageUrl,
    promotedContent,
  } = academy
  const { quickLinks } = promotedContent

  const hasQuickLinks = quickLinks?.links?.length > 0
  const hasOverrideImage = quickLinks?.overrideImageURL
  const theme = useTheme()
  const classes = useStyles({ bgColor })
  const contactInfo = buildContactInfo(academy)

  return (
    <>
      {featuredImageUrl && (
        <Hero
          background={{ url: featuredImageUrl }}
          gradient={theme.palette.background.reverseGradient}
          variant="overlap"
          className={classes.featuredImg}
        />
      )}
      <Box className={classes.outerContainer}>
        <Container>
          <Grid container>
            <Grid item xs={12}>
              <Header academy={academy} />
              <ContactInfoSection {...contactInfo} />
            </Grid>
            <Grid container justify="space-between">
              <Grid
                item
                xs={12}
                md={hasOverrideImage || hasQuickLinks ? 8 : 12}
                className={classes.descriptionSection}
              >
                <Description description={description} />
              </Grid>

              {hasOverrideImage && (
                <Grid item xs={12} md={4} className={classes.quickLinksSection}>
                  <img src={quickLinks?.overrideImageURL} alt="Quick Links decoration" className={classes.quickLinksImage} />
                </Grid>
              )}
              {!hasOverrideImage && hasQuickLinks && (
                <Grid item xs={12} md={4} className={classes.quickLinksSection}>
                  <QuickLinks {...quickLinks} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  )
}

Information.propTypes = {
  academy: PropTypes.shape({
    id: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    logoImageUrl: PropTypes.string,
    featuredImageUrl: PropTypes.string,
    promotedContent: PropTypes.shape({
      quickLinks: PropTypes.shape({
        imageUrl: PropTypes.string,
        overrideImageURL: PropTypes.string,
        links: PropTypes.arrayOf(PropTypes.shape({
          title: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
        })).isRequired,
      }),
    }),
    facilities: PropTypes.arrayOf(
      PropTypes.shape({
        address1: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string,
        phone: PropTypes.string,
        url: PropTypes.string,
      }),
    ),
  }).isRequired,
  bgColor: PropTypes.string,
}

export default Information
